// Bagless call client for plugins/vapi-r.
// The private key stays on /vapi-agent/proxy. This client only receives a websocketCallUrl
// and streams PCM16LE both ways. No Daily room, no WebRTC join.
import workletSource from '!!raw-loader!../plugins/vapi-r/pcm-encoder-worklet.js';

const SAMPLE_RATE = 16000;
const ENCODER_FRAME_SIZE = 320; // 20ms at 16 kHz
const CLIENT_MESSAGES = [
    'transcript',
    'tool-calls',
    'function-call',
    'speech-update',
    'status-update',
    'conversation-update',
    'hang',
];

const TRANSPORT = {
    provider: 'vapi.websocket',
    audioFormat: {
        format: 'pcm_s16le',
        container: 'raw',
        sampleRate: SAMPLE_RATE,
    },
};

function workletText() {
    if (typeof workletSource === 'string') return workletSource;
    if (workletSource && typeof workletSource.default === 'string') return workletSource.default;
    return '';
}

function withClientMessages(overrides) {
    const base = overrides && typeof overrides === 'object' ? { ...overrides } : {};
    const existing = Array.isArray(base.clientMessages) ? base.clientMessages : [];
    base.clientMessages = [...new Set([...existing, ...CLIENT_MESSAGES])];
    return base;
}

export function createVapiRCall() {
    const listeners = new Map();
    let socket = null;
    let audioContext = null;
    let micStream = null;
    let ownsMicStream = false;
    let encoderNode = null;
    let nextPlaybackTime = 0;
    let muted = false;
    let stopped = false;
    let started = false;
    let workletUrl = null;
    let previousLogAt = performance.now();

    function logEvent(label, detail) {
        const now = performance.now();
        const deltaMs = Math.round(now - previousLogAt);
        previousLogAt = now;
        const line = `[VAPI +${deltaMs}ms] ${label}`;
        if (detail === undefined) console.log(line);
        else console.log(line, detail);
    }

    function summarizeEvent(type, payload) {
        if (payload instanceof Error) return payload.message;
        if (type !== 'message' || !payload || typeof payload !== 'object') return payload;
        const summary = { type: payload.type };
        if (payload.status) summary.status = payload.status;
        if (payload.role) summary.role = payload.role;
        if (payload.type === 'transcript') summary.transcriptType = payload.transcriptType;
        if (payload.endedReason) summary.endedReason = payload.endedReason;
        if (payload.callId || payload.call?.id) summary.callId = payload.callId || payload.call?.id;
        return summary;
    }

    function emit(type, payload) {
        const quietTranscript = type === 'message' && payload?.type === 'transcript';
        if (!quietTranscript) logEvent(type, summarizeEvent(type, payload));
        const set = listeners.get(type);
        if (!set) return;
        set.forEach((fn) => {
            try { fn(payload); } catch (err) { console.warn('[vapi-r] listener failed', type, err); }
        });
    }

    function unwrapMessage(raw) {
        if (!raw || typeof raw !== 'object') return null;
        if (raw.message && typeof raw.message === 'object' && raw.message.type) return raw.message;
        return raw;
    }

    function cleanupAudio() {
        try { encoderNode?.disconnect(); } catch (e) { /* already gone */ }
        encoderNode = null;
        if (ownsMicStream) {
            micStream?.getTracks?.().forEach((track) => track.stop());
        }
        micStream = null;
        ownsMicStream = false;
        if (audioContext) {
            audioContext.close().catch(() => {});
            audioContext = null;
        }
        if (workletUrl) {
            URL.revokeObjectURL(workletUrl);
            workletUrl = null;
        }
        nextPlaybackTime = 0;
    }

    function playPcmFrame(data) {
        if (!audioContext) return;
        const int16 = new Int16Array(data);
        const float32 = new Float32Array(int16.length);
        for (let i = 0; i < int16.length; i++) {
            const sample = int16[i];
            float32[i] = sample / (sample < 0 ? 0x8000 : 0x7fff);
        }
        const buffer = audioContext.createBuffer(1, float32.length, SAMPLE_RATE);
        buffer.copyToChannel(float32, 0);
        const source = audioContext.createBufferSource();
        source.buffer = buffer;
        source.connect(audioContext.destination);
        const startAt = Math.max(audioContext.currentTime, nextPlaybackTime);
        source.start(startAt);
        nextPlaybackTime = startAt + buffer.duration;
    }

    async function readBinary(data) {
        if (data instanceof ArrayBuffer) return data;
        if (data instanceof Blob) return data.arrayBuffer();
        return null;
    }

    function handleTextMessage(raw) {
        let parsed;
        try { parsed = JSON.parse(raw); } catch (e) { return; }
        const message = unwrapMessage(parsed);
        if (!message) return;
        if (message.type === 'status-update' && message.status === 'in-progress') {
            logEvent('agent started');
        }
        emit('message', message);
        if (message.type === 'speech-update' && message.status === 'started' && message.role === 'assistant') {
            emit('speech-start');
        }
        if (message.type === 'speech-update' && message.status === 'stopped' && message.role === 'assistant') {
            emit('speech-end');
        }
        if (message.type === 'status-update' && message.status === 'ended') {
            finish('ended');
        }
    }

    function finish(reason) {
        if (stopped) return;
        stopped = true;
        const ws = socket;
        socket = null;
        cleanupAudio();
        if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
            try { ws.close(1000, reason || 'ended'); } catch (e) { /* ignore */ }
        }
        logEvent('call ended', { reason: reason || 'ended' });
        if (started) emit('call-end');
    }

    async function connect(websocketUrl, mediaStream) {
        audioContext = new AudioContext({ sampleRate: SAMPLE_RATE });
        if (audioContext.state === 'suspended') {
            await audioContext.resume().catch(() => {});
        }
        const sourceText = workletText();
        if (!sourceText) throw new Error('PCM encoder worklet missing');
        workletUrl = URL.createObjectURL(new Blob([sourceText], { type: 'application/javascript' }));
        await audioContext.audioWorklet.addModule(workletUrl);

        if (mediaStream && mediaStream.getAudioTracks?.().some((track) => track.readyState === 'live')) {
            micStream = mediaStream;
            ownsMicStream = false;
        } else {
            micStream = await navigator.mediaDevices.getUserMedia({
                audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
            });
            ownsMicStream = true;
        }
        micStream.getAudioTracks().forEach((track) => { track.enabled = true; });

        const ws = new WebSocket(websocketUrl);
        socket = ws;
        ws.binaryType = 'arraybuffer';

        logEvent('connecting');
        await new Promise((resolve, reject) => {
            const timer = setTimeout(() => reject(new Error('WebSocket connection timed out')), 15000);
            ws.addEventListener('open', () => {
                clearTimeout(timer);
                logEvent('websocket connected');
                resolve();
            }, { once: true });
            ws.addEventListener('error', () => { clearTimeout(timer); reject(new Error('WebSocket connection failed')); }, { once: true });
        });

        ws.addEventListener('message', (event) => {
            if (typeof event.data === 'string') {
                handleTextMessage(event.data);
                return;
            }
            readBinary(event.data).then((buffer) => {
                if (buffer) playPcmFrame(buffer);
            }).catch(() => {});
        });
        ws.addEventListener('close', () => finish('closed'));
        ws.addEventListener('error', () => {
            if (!started) emit('error', new Error('WebSocket connection failed'));
        });

        const micSource = audioContext.createMediaStreamSource(micStream);
        encoderNode = new AudioWorkletNode(audioContext, 'pcm-encoder', {
            processorOptions: {
                frameSize: ENCODER_FRAME_SIZE,
                targetSampleRate: SAMPLE_RATE,
            },
        });
        encoderNode.port.onmessage = (event) => {
            if (ws.readyState === WebSocket.OPEN) ws.send(event.data);
        };
        if (muted) encoderNode.port.postMessage({ type: 'mute', value: true });

        const silentSink = audioContext.createGain();
        silentSink.gain.value = 0;
        micSource.connect(encoderNode).connect(silentSink).connect(audioContext.destination);
        logEvent('connection established');
    }

    return {
        on(type, fn) {
            if (!listeners.has(type)) listeners.set(type, new Set());
            listeners.get(type).add(fn);
            return this;
        },
        setMuted(next) {
            muted = !!next;
            encoderNode?.port.postMessage({ type: 'mute', value: muted });
        },
        send(message) {
            if (!socket || socket.readyState !== WebSocket.OPEN) return;
            socket.send(JSON.stringify(message));
        },
        stop() {
            if (socket?.readyState === WebSocket.OPEN) {
                try { socket.send(JSON.stringify({ type: 'end-call' })); } catch (e) { /* ignore */ }
            }
            finish('user ended call');
        },
        async start(options = {}) {
            stopped = false;
            started = false;
            previousLogAt = performance.now();
            const proxyUrl = String(options.proxyUrl || '').replace(/\/$/, '');
            const token = String(options.token || '').trim();
            if (!proxyUrl || !token) throw new Error('Missing VAPI proxy');

            const payload = { transport: TRANSPORT };
            if (options.squadId) {
                payload.squadId = options.squadId;
                payload.squadOverrides = withClientMessages(options.squadOverrides);
            } else {
                payload.assistantId = options.assistantId;
                payload.assistantOverrides = withClientMessages(options.assistantOverrides);
            }

            emit('call-start-progress', { stage: 'create-call' });
            const res = await fetch(`${proxyUrl}/call`, {
                method: 'POST',
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                const err = new Error(data?.error || data?.message || `Failed to create call (${res.status})`);
                emit('call-start-failed', err);
                throw err;
            }
            const websocketUrl = data?.transport?.websocketCallUrl || data?.websocketCallUrl;
            const callId = data?.id || data?.callId;
            if (!websocketUrl) {
                const err = new Error('VAPI did not return a websocket URL');
                emit('call-start-failed', err);
                throw err;
            }
            emit('call-start-success', { callId, id: callId, call: { id: callId } });

            try {
                await connect(websocketUrl, options.mediaStream || null);
            } catch (err) {
                cleanupAudio();
                if (socket) {
                    try { socket.close(); } catch (e) { /* ignore */ }
                    socket = null;
                }
                emit('call-start-failed', err);
                throw err;
            }
            if (stopped) {
                const err = new Error('Call ended before it started');
                emit('call-start-failed', err);
                throw err;
            }
            started = true;
            emit('call-start', { callId });
        },
    };
}
